import MutateChargingServiceBasicInformationForm, {
  MutateChargingServiceBasicInformationFormValues,
} from '../../ui/places/MutateChargingServiceBasicInformationForm.tsx';
import useCreateChargingService from '../../../hooks/charging-services/useCreateChargingService.ts';
import {
  CreateChargingServiceRequest,
  UpdateChargingServiceOperationHoursRequest,
} from '../../../api/requests/charging-service-request.ts';
import { useState } from 'react';
import useUpdateChargingServiceOperationHours from '../../../hooks/charging-services/useUpdateChargingServiceOperationHours.ts';
import useUpdateChargingServiceStoreIds from '../../../hooks/charging-services/useUpdateChargingServiceStoreIds.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import MutateChargingServiceOperationHoursForm, {
  MutateChargingServiceOperationHoursFormValues,
} from '../../ui/places/MutateChargingServiceOperationHoursForm.tsx';
import {
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Stepper,
  Text,
  TextInput,
} from '@mantine/core';
import MutateChargingServiceImagesForm from '../../ui/places/MutateChargingServiceImagesForm.tsx';
import useChargingService from '../../../hooks/charging-services/useChargingService.ts';
import { PricingSource } from '../../../models/PlaceService.ts';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ErrorResponse } from '../../../api/responses/error-response.ts';
import { JUPITER_BASE_URL, parseStoreUrl } from '../../../utils/storeUrl.ts';

const phoneUtil = PhoneNumberUtil.getInstance();

interface AddChargingServiceModalProps {
  opened: boolean;
  onClose: () => void;
  placeId: string;
  placeName: string;
  onSuccess?: () => void;
}

const AddChargingServiceModal = ({
  opened,
  onClose,
  placeId,
  placeName,
  onSuccess,
}: AddChargingServiceModalProps) => {
  const [active, setActive] = useState(0);

  const [chargingServiceId, setChargingServiceId] = useState<string | null>(
    null,
  );

  const [storeIdUrl, setStoreIdUrl] = useState('');
  const [storeIdError, setStoreIdError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { mutateAsync: createChargingService } = useCreateChargingService();

  const { data: chargingService, isLoading: isChargingServiceLoading } =
    useChargingService(chargingServiceId);

  const { mutateAsync: updateChargingServiceOperationHours } =
    useUpdateChargingServiceOperationHours();

  const {
    mutateAsync: updateChargingServiceStoreIds,
    isPending: isStoreIdPending,
  } = useUpdateChargingServiceStoreIds();

  // 新增充電基本資訊 mutation
  const {
    mutate: handleBasicInformationSubmit,
    isPending: isBasicInformationPending,
  } = useMutation<
    { id: string },
    ErrorResponse,
    MutateChargingServiceBasicInformationFormValues
  >({
    mutationFn: async (formValues) => {
      const parsedPhoneNumber =
        formValues.phoneNumberCountryCode !== '' &&
        formValues.phoneNumberValue !== ''
          ? phoneUtil.parse(
              `${formValues.phoneNumberCountryCode}${formValues.phoneNumberValue}`,
            )
          : null;

      const request: CreateChargingServiceRequest = {
        placeId: placeId,
        name: formValues.name.trim(),
        brandId: formValues.brandId ?? '',
        serviceStatus: formValues.serviceStatus,
        pricingSource: 'UNKNOWN' as PricingSource,
        operationHours: {
          weeklySchedule: [
            {
              dayOfWeek: 'MONDAY',
              isOpen: 'OPEN',
              timeSlots: [{ startTime: '00:00:00', endTime: '23:59:00' }],
            },
            {
              dayOfWeek: 'TUESDAY',
              isOpen: 'OPEN',
              timeSlots: [{ startTime: '00:00:00', endTime: '23:59:00' }],
            },
            {
              dayOfWeek: 'WEDNESDAY',
              isOpen: 'OPEN',
              timeSlots: [{ startTime: '00:00:00', endTime: '23:59:00' }],
            },
            {
              dayOfWeek: 'THURSDAY',
              isOpen: 'OPEN',
              timeSlots: [{ startTime: '00:00:00', endTime: '23:59:00' }],
            },
            {
              dayOfWeek: 'FRIDAY',
              isOpen: 'OPEN',
              timeSlots: [{ startTime: '00:00:00', endTime: '23:59:00' }],
            },
            {
              dayOfWeek: 'SATURDAY',
              isOpen: 'OPEN',
              timeSlots: [{ startTime: '00:00:00', endTime: '23:59:00' }],
            },
            {
              dayOfWeek: 'SUNDAY',
              isOpen: 'OPEN',
              timeSlots: [{ startTime: '00:00:00', endTime: '23:59:00' }],
            },
          ],
          holidaySchedule: [],
          temporaryClosures: [],
          timezone: 'UTC+8',
        },
        phoneNumber: {
          countryCode: formValues.phoneNumberCountryCode ?? '',
          number: parsedPhoneNumber
            ? phoneUtil.getNationalSignificantNumber(parsedPhoneNumber)
            : '',
        },
        website: formValues.website,
        notes: formValues.notes,
        tags: formValues.tags,
        paymentMethods: formValues.paymentMethods,
      };

      return await createChargingService(request);
    },
    onSuccess: (data) => {
      setChargingServiceId(data.id);
      setActive(1);
    },
    onError: (error) => {
      notifyError('新增充電服務失敗', error.message);
    },
  });

  // 綁定營運商店（可選填，跳過則直接進到下一步）
  const handleStoreIdSubmit = async () => {
    if (!storeIdUrl.trim()) {
      setActive(2);
      return;
    }

    let entry: ReturnType<typeof parseStoreUrl>;
    try {
      entry = parseStoreUrl(storeIdUrl, 'CHARGING');
    } catch (error) {
      setStoreIdError(error instanceof Error ? error.message : '網址格式錯誤');
      return;
    }

    try {
      await updateChargingServiceStoreIds({
        serviceId: chargingServiceId,
        storeIds: [entry],
      });
      await queryClient.invalidateQueries({
        queryKey: ['chargingServices', chargingServiceId],
        exact: true,
      });
      setActive(2);
    } catch (error) {
      notifyError(
        '綁定營運商店失敗',
        error instanceof Error ? error.message : String(error),
      );
    }
  };

  // 更新充電營業時間 mutation
  const {
    mutate: handleOperationHoursSubmit,
    isPending: isOperationHoursPending,
  } = useMutation<
    void,
    ErrorResponse,
    MutateChargingServiceOperationHoursFormValues
  >({
    mutationFn: async (formValues) => {
      const request: UpdateChargingServiceOperationHoursRequest = {
        serviceId: chargingServiceId,
        operationHours: formValues.operationHours,
      };
      await updateChargingServiceOperationHours(request);
    },
    onSuccess: () => {
      setActive(3);
    },
    onError: (error) => {
      notifyError('新增充電服務失敗', error.message);
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="新增充電服務"
      centered
      closeOnClickOutside={false}
      size={659}
      styles={{
        title: { fontWeight: 700, fontSize: 16 },
        header: { paddingBottom: 24 },
      }}
    >
      <Stepper
        size={'sm'}
        active={active}
        allowNextStepsSelect={false}
        styles={{
          root: {
            justifyItems: 'center',
          },
          steps: {
            width: 600,
            paddingBottom: 24,
          },
          content: {
            width: 627,
            paddingTop: 0,
          },
        }}
      >
        {/* Step 1: 充電服務基本資料 */}
        <Stepper.Step label="基本資訊">
          <MutateChargingServiceBasicInformationForm
            initialValues={{
              name: placeName,
              serviceStatus: null,
              brandId: '',
              phoneNumberCountryCode: '',
              phoneNumberValue: '',
              website: '',
              notes: '',
              tags: [],
              paymentMethods: [],
            }}
            onSubmit={handleBasicInformationSubmit}
            isSubmitting={isBasicInformationPending}
            onCancel={onClose}
            isUpdating={false}
          />
        </Stepper.Step>

        {/* Step 2: 綁定營運商店（可選） */}
        <Stepper.Step label={'營運商店'}>
          <Stack gap={'md'}>
            <Text size={'sm'} c={'dimmed'}>
              如果這個服務已經在 autoplus
              有對應的商店，可以貼上網址進行綁定，下一步的營業時間將會直接帶入
              autoplus 現有資料。也可以先跳過，之後再從服務詳情頁面綁定。
            </Text>
            <TextInput
              label={'營運商店'}
              placeholder={`${JUPITER_BASE_URL}/chain_stores/{id}/...`}
              value={storeIdUrl}
              onChange={(e) => {
                setStoreIdUrl(e.currentTarget.value);
                setStoreIdError(null);
              }}
              error={storeIdError}
            />
            <Group justify={'flex-end'}>
              <Button variant={'outline'} onClick={onClose}>
                取消
              </Button>
              <Button
                onClick={handleStoreIdSubmit}
                loading={isStoreIdPending}
                loaderProps={{ type: 'oval', size: 'xs' }}
              >
                下一步
              </Button>
            </Group>
          </Stack>
        </Stepper.Step>

        {/* Step 3: 充電服務營業時間 */}
        <Stepper.Step label="營業時間">
          {isChargingServiceLoading ? (
            <Loader />
          ) : (
            <MutateChargingServiceOperationHoursForm
              initialValues={{
                operationHours: chargingService?.operationHours,
              }}
              onSubmit={handleOperationHoursSubmit}
              isSubmitting={isOperationHoursPending}
              onCancel={onClose}
              isUpdating={false}
            />
          )}
        </Stepper.Step>

        {/* Step 4: 充電服務圖片 */}
        <Stepper.Step label="上傳圖片">
          {isChargingServiceLoading ? (
            <Loader />
          ) : (
            <MutateChargingServiceImagesForm
              key={chargingServiceId}
              initialValues={{
                chargingServiceId: chargingServiceId,
              }}
              onSubmit={onSuccess}
              onCancel={onClose}
              isUpdating={false}
            />
          )}
        </Stepper.Step>
      </Stepper>
    </Modal>
  );
};

export { AddChargingServiceModal };
